import React, { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "./supabase/auth";

interface AuthContextValue {
  session: any;
  user: any;
  isAdmin: boolean;
  loading: boolean;
  recovering: boolean;
  signOut: () => Promise<void>;
  clearRecovering: () => void;
}

const AuthContext = createContext<AuthContextValue>({
  session: null,
  user: null,
  isAdmin: false,
  loading: true,
  recovering: false,
  signOut: async () => {},
  clearRecovering: () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

export default function AuthProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [session, setSession] = useState<any>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [recovering, setRecovering] = useState(false);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      if (s?.user?.email) {
        checkAdmin(s.user.email);
      } else {
        setLoading(false);
      }
    }).catch(() => {
      // A rejected getSession() would leave loading=true forever (spinner).
      setLoading(false);
    });

    // Listen for auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s);
      if (event === "PASSWORD_RECOVERY") {
        setRecovering(true);
      }
      if (s?.user?.email) {
        // Defer: supabase-js v2 warns that awaiting supabase calls directly
        // inside onAuthStateChange can deadlock on token refresh, because
        // the client lock is still held while the event is emitted.
        const email = s.user.email;
        setTimeout(() => checkAdmin(email), 0);
      } else {
        setIsAdmin(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  async function checkAdmin(email: string) {
    const { data } = await supabase
      .from("admins")
      .select("id")
      .eq("email", email.toLowerCase())
      .maybeSingle();
    setIsAdmin(!!data);
    setLoading(false);
  }

  const signOut = async () => {
    await supabase.auth.signOut();
    window.location.hash = "";
    window.location.reload();
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        user: session?.user ?? null,
        isAdmin,
        loading,
        recovering,
        signOut,
        clearRecovering: () => setRecovering(false),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
